// src/pages/access/MaintenancePanel.tsx — Phase 9 c14 (admin toggle).
//
// Lives inside the User Access shell. Admin-only (the page itself is
// requireSsoAdmin). Toggles the global maintenance-mode flag. When ON,
// all non-admin users see the holding page in MaintenanceHoldingPage.tsx
// on their next /api/me poll.
//
// The textarea adds an optional custom message rendered below the fixed
// default banner. The default banner text is owned server-side and shown
// here verbatim so the admin knows what users see.
//
// Confirmation modal on ENABLE — the effect is global and immediate.
// No confirmation on DISABLE since restoring access is harmless.

import { useState } from "react";
import { apiFetch, ApiError } from "../../api";
import type { SystemMaintenanceState } from "../../auth";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow } from "../../components/ui";

interface MaintenancePanelProps {
  state: SystemMaintenanceState;
  onChanged: () => void;
}

interface SetResponse {
  enabled: boolean;
  message: string | null;
  updatedAt: string;
  updatedBy: string | null;
  defaultBanner: string;
}

const MESSAGE_MAX = 2000;

export function MaintenancePanel({ state, onChanged }: MaintenancePanelProps) {
  const [enabled, setEnabled] = useState(state.active);
  const [message, setMessage] = useState(state.message ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function submit(nextEnabled: boolean) {
    setError(null);
    setFlash(null);
    setSaving(true);
    try {
      await apiFetch<SetResponse>("/api/access/maintenance", {
        method: "POST",
        body: JSON.stringify({
          enabled: nextEnabled,
          message: message.trim() === "" ? null : message.trim(),
        }),
      });
      setEnabled(nextEnabled);
      setFlash(nextEnabled ? "Maintenance mode is now ON for non-admin users." : "Maintenance mode is OFF.");
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save maintenance state.");
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  }

  function attemptSave() {
    if (!enabled) {
      // Currently OFF, admin wants to turn ON — confirm.
      setConfirming(true);
      return;
    }
    // Currently ON — save with current state (allows updating message
    // without flipping the switch).
    submit(true);
  }

  return (
    <Card>
      <Eyebrow c={enabled ? C.signalDk : C.brassDk}>System maintenance</Eyebrow>
      <div
        style={{
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 13,
          color: C.inkSoft,
          marginTop: 4,
          marginBottom: 10,
          lineHeight: 1.45,
        }}
      >
        Block all non-admin users from the app. Admins are not blocked so you can disable the
        toggle again. Effect is immediate on the next /api/me poll (≤60 s for active sessions).
      </div>

      <div
        style={{
          padding: "12px 14px",
          borderRadius: 12,
          background: C.paperWarm,
          border: `1px solid ${C.hairlineLt}`,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontFamily: F.body,
            fontSize: 11,
            fontWeight: 800,
            color: C.inkMute,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 6,
          }}
        >
          Default banner (always shown)
        </div>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 13,
            color: C.ink,
            lineHeight: 1.5,
          }}
        >
          {state.defaultBanner}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <span
          style={{
            fontFamily: F.body,
            fontWeight: 800,
            fontSize: 14,
            color: C.ink,
          }}
        >
          Maintenance mode
        </span>
        <button
          type="button"
          onClick={() => {
            if (saving) return;
            if (enabled) {
              // Toggle from ON → OFF immediately, no confirmation.
              submit(false);
            } else {
              // Toggle from OFF → ON: show confirmation first.
              setConfirming(true);
            }
          }}
          disabled={saving}
          aria-pressed={enabled}
          style={{
            padding: "8px 16px",
            borderRadius: 999,
            border: `1.5px solid ${enabled ? C.signalDk : C.hairline}`,
            background: enabled ? C.signal : C.card,
            color: enabled ? "#FFFCEC" : C.inkSoft,
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 0.4,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {enabled ? "ON" : "OFF"}
        </button>
      </div>

      <div>
        <label
          style={{
            display: "block",
            fontFamily: F.body,
            fontSize: 11,
            fontWeight: 800,
            color: C.inkMute,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 6,
          }}
        >
          Optional message (max {MESSAGE_MAX} chars)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          disabled={!enabled && message.trim() === ""}
          rows={4}
          placeholder="Optional context shown below the banner. Leave blank for default-only."
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${C.hairline}`,
            background: C.card,
            fontFamily: F.body,
            fontSize: 14,
            color: C.ink,
            resize: "vertical",
            minHeight: 80,
          }}
        />
        <div
          style={{
            fontFamily: F.mono,
            fontSize: 11,
            color: C.inkMute,
            textAlign: "right",
            marginTop: 4,
          }}
        >
          {message.length} / {MESSAGE_MAX}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 10,
            borderRadius: 10,
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            color: C.signalDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
            marginTop: 10,
          }}
        >
          {error}
        </div>
      )}

      {flash && (
        <div
          style={{
            padding: 10,
            borderRadius: 10,
            background: C.sageBg,
            border: `1px solid ${C.sageLt}`,
            color: C.sageDk,
            fontFamily: F.body,
            fontWeight: 700,
            fontSize: 13,
            marginTop: 10,
          }}
        >
          {flash}
        </div>
      )}

      <Button
        variant={enabled ? "danger" : "primary"}
        fullWidth
        style={{ marginTop: 12 }}
        disabled={saving}
        onClick={attemptSave}
      >
        {saving ? "Saving…" : enabled ? "Update message / Keep ON" : "Enable maintenance mode"}
      </Button>

      {confirming && (
        <ConfirmEnableModal
          message={message.trim()}
          onCancel={() => setConfirming(false)}
          onConfirm={() => submit(true)}
          saving={saving}
        />
      )}
    </Card>
  );
}

function ConfirmEnableModal({
  message,
  onCancel,
  onConfirm,
  saving,
}: {
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  saving: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(43,32,20,0.55)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
        }}
      >
        <Card>
          <Eyebrow c={C.signalDk}>Enable maintenance mode?</Eyebrow>
          <p
            style={{
              fontFamily: F.serif,
              fontSize: 15,
              color: C.ink,
              fontWeight: 600,
              lineHeight: 1.55,
              marginTop: 8,
            }}
          >
            This will block all non-admin users from the app immediately. Admins (including you)
            continue to have full access.
          </p>
          {message && (
            <div
              style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 10,
                background: C.paperWarm,
                border: `1px solid ${C.hairlineLt}`,
                fontFamily: F.serif,
                fontStyle: "italic",
                fontSize: 13,
                color: C.ink,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ fontStyle: "normal", color: C.inkMute, fontSize: 11, letterSpacing: 0.5 }}>
                YOUR MESSAGE
              </strong>
              <div style={{ marginTop: 4 }}>{message}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <Button variant="ghost" fullWidth onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="danger" fullWidth onClick={onConfirm} disabled={saving}>
              {saving ? "Enabling…" : "Enable for everyone"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
